import { ReactNode } from 'react';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { DATA_CHECK_ERROR_CODE } from 'support/helpers/const/const';
import { IssueResolverType, MessageV2DTO, PartnerDTO } from 'support/types';
import { DisplayModes } from 'components/DataTable/Types';

import { ContentBanner, DatachecksErrorBanner, GenericBlockingErrorBanner } from './components';

type WebEDIMessageErrorBannerProps = {
  mode: DisplayModes;
  issueResolver?: IssueResolverType;
  isCurrentPartnerReceiver?: boolean;
  senderPartner?: PartnerDTO | null | undefined;
  receiverPartner?: PartnerDTO | null | undefined;
  isLoadingDatachecksErrors?: boolean;
} & ({ blockingError: MessageV2DTO['blocking_error'] } | { content: ReactNode });

export const WebEDIMessageErrorBanner = (props: WebEDIMessageErrorBannerProps) => {
  const { mode, issueResolver, isCurrentPartnerReceiver, senderPartner, receiverPartner, isLoadingDatachecksErrors } =
    props;
  const blockingError = 'blockingError' in props ? props.blockingError : undefined;
  const content = 'content' in props ? props.content : undefined;
  const { service } = useErrorContext<WebEDIError>();
  const numberOfErrors = service.useNumberOfActiveErrors();

  if (content) {
    return <ContentBanner>{content}</ContentBanner>;
  }

  if (blockingError) {
    const isDatachecksError = blockingError.code === DATA_CHECK_ERROR_CODE;

    if (isDatachecksError) {
      return (
        <DatachecksErrorBanner
          mode={mode}
          issueResolver={issueResolver}
          isCurrentPartnerReceiver={isCurrentPartnerReceiver}
          senderPartner={senderPartner}
          receiverPartner={receiverPartner}
          isLoadingDatachecksErrors={isLoadingDatachecksErrors}
          blockingError={blockingError}
          numberOfErrors={numberOfErrors}
        />
      );
    }

    return (
      <GenericBlockingErrorBanner
        blockingError={blockingError}
        issueResolver={issueResolver}
        isCurrentPartnerReceiver={isCurrentPartnerReceiver}
      />
    );
  }

  // In create mode we don't have a blocking error, but we still need to show this banner
  if (numberOfErrors > 0) {
    return <DatachecksErrorBanner mode={mode} issueResolver={issueResolver} numberOfErrors={numberOfErrors} />;
  }

  return null;
};
